import styled from '@benzinga/themetron';
import { PermissionedComponent } from '@benzinga/user-context';
import React from 'react';
import { Lock } from '@benzinga/themed-icons';

import {
  useHasUserTrialed,
  useIsUserLoggedIn,
  useIsUserTrialing,
  useUserHasActiveSubscription,
} from '@benzinga/user-context';

type TextProps = Partial<{
  denied_logged_in: string;
  denied_logged_in_has_trialed: string;
  denied_logged_in_is_trialing: string;
  denied_logged_out: string;
}>;

type Props = React.ComponentProps<typeof PermissionedComponent> & {
  text?: TextProps | string;
};

const getText = (texts: TextProps, access: keyof TextProps): string | undefined => {
  switch (access) {
    case 'denied_logged_in':
      return texts['denied_logged_in'];
    case 'denied_logged_in_has_trialed':
      return texts['denied_logged_in_has_trialed'] ?? texts['denied_logged_in'];
    case 'denied_logged_in_is_trialing':
      return texts['denied_logged_in_is_trialing'] ?? texts['denied_logged_in'];
    case 'denied_logged_out':
      return texts['denied_logged_out'];
  }
};

export const PermissionOverlay: React.FC<React.PropsWithChildren<Props>> = props => {
  const isLoggedIn = useIsUserLoggedIn();
  const isTrialing = useIsUserTrialing(props?.slug?.product);
  const hasTrialed = useHasUserTrialed(props?.slug?.product);
  const hasSubscription = useUserHasActiveSubscription(props?.slug?.product);

  return (
    <PermissionedComponent {...props}>
      {access =>
        access ? (
          props.children
        ) : (
          <Holder>
            <Text>
              <LockIcon />
              <h1>
                {(typeof props.text === 'object'
                  ? getText(
                      props.text,
                      isLoggedIn
                        ? hasSubscription
                          ? isTrialing
                            ? 'denied_logged_in_is_trialing'
                            : 'denied_logged_in'
                          : hasTrialed
                            ? 'denied_logged_in_has_trialed'
                            : 'denied_logged_in'
                        : 'denied_logged_out',
                    )
                  : props.text) ?? `You don't seem to have access to this feature`}
              </h1>
            </Text>
          </Holder>
        )
      }
    </PermissionedComponent>
  );
};

const Holder = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Text = styled.div`
  text-align: center;
  align-items: center;
  padding: 100px;
  margin: auto;
  font-size: 20px;
`;

const LockIcon = styled(Lock)`
  fill: ${props => props.theme.colors.accent};
  height: 3.5em;
  width: 3.5em;
  padding-bottom: 1em;
`;
